// Single source of truth for runtime URLs that differ between web and native
// (Capacitor). On native the WebView loads from https://localhost, so
// window.location.origin / host are meaningless — every API, WS, and OAuth call
// must resolve through here instead. See tasks/android-frontend.md Phase 0.
import { Capacitor } from '@capacitor/core'

export const isNative = Capacitor.isNativePlatform()

// REST API base. Web: same origin. Native: the real backend host (baked in at
// build via .env.native). Keep the /api/v1 suffix; client.ts appends the path.
export const API_BASE = isNative
  ? import.meta.env.VITE_NATIVE_API_BASE
  : `${window.location.origin}/api/v1`

// WebSocket ORIGIN only (scheme + host). useChatConnection appends the
// /api/v1/ws/{userId}?token=... path — matching the backend WS route, NOT /ws.
export const WS_BASE = isNative
  ? import.meta.env.VITE_NATIVE_WS_BASE
  : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`

// Yandex OAuth redirect_uri sent to Yandex. Native uses the backend callback
// (the SAME https URL as web — Yandex redirects there, the backend exchanges the
// code, then 302s to OAUTH_NATIVE_RETURN). NOT the scheme itself: Yandex won't
// redirect to a custom scheme, and the token exchange is redirect_uri-agnostic.
export const OAUTH_REDIRECT_URI = isNative
  ? `${import.meta.env.VITE_NATIVE_API_BASE}/auth/yandex/callback`
  : `${window.location.origin}/api/v1/auth/yandex/callback`

// The custom scheme the backend 302s back to after exchanging the code; Android
// opens the app via the manifest intent-filter and main.jsx's appUrlOpen matches
// this prefix. Native only. See Phase 2.5.
export const OAUTH_NATIVE_RETURN = isNative
  ? `${import.meta.env.VITE_NATIVE_OAUTH_SCHEME}://oauth/yandex`
  : ''

// Native Metrica counter ID baked in at build (Phase 3). Web uses its own
// counter initialized directly in lib/metrica.ts.
export const NATIVE_METRIKA_ID = isNative ? import.meta.env.VITE_NATIVE_METRIKA_ID : ''

// Backend origin (scheme + host, no path) — derived from API_BASE so there is
// only one place to point at a stand. Empty on web, where relative URLs already
// resolve to the right host.
export const API_ORIGIN = (() => {
  if (!isNative) return ''
  try {
    return new URL(API_BASE).origin
  } catch {
    return ''
  }
})()

// Resolve a media URL for direct browser loading (<img>/<video>/<audio> src).
//
// The backend hands out root-relative paths — `/api/v1/media/<file>`, see
// http_api/message_handler.py:354. On web that resolves to the backend because
// the document and the API share an origin. On native the document origin is
// the bundled app (https://humanteq.app, served locally by Capacitor), so the
// same path looks for the file INSIDE the APK, 404s, and the element's onError
// swaps in "Не удалось загрузить …". Absolutizing is enough: /media/{filename}
// is unauthenticated (http_api/router.py:2867), and a media element could not
// send an Authorization header anyway.
// Generic so a `string` in stays a `string` out — callers pass it straight into
// a media element's src, which is not nullable.
export function mediaSrc<T extends string | null | undefined>(url: T): T {
  if (!isNative || !url || !url.startsWith('/')) return url
  return `${API_ORIGIN}${url}` as T
}
